/**
 * Compact list of appointments tied to a single workflow case. Lives
 * on the case-detail page so the staff member can see every callback
 * they (or the manager) scheduled for the client on this case
 * without leaving the page.
 *
 * Sections:
 *   - Upcoming (status=scheduled, starts_at >= now)
 *   - Past     (status in {completed, cancelled, no_show}, or
 *                starts_at < now)  — collapsed by default
 *
 * Refresh hooks: this panel re-fetches on any of
 *   appointment.created / cancelled / reassigned / claimed / imminent
 * delivered through the existing event stream. The parent already
 * owns the SSE subscription; we expose a `refetch` ref via a render
 * prop so the parent can call it.
 */
import { useCallback, useEffect, useState } from 'react';

import schedulingService, {
  type AppointmentSummary,
} from '../../services/scheduling.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import { readerLocale } from '../../i18n/language';

interface Props {
  caseId: string;
  /** When provided, the panel hands back its refetch fn so the parent
   *  can trigger a refresh from an SSE event handler. */
  registerRefetch?: (refetch: () => Promise<void>) => void;
}

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(readerLocale(), {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function partyName(p: AppointmentSummary['agent']): string {
  if (!p) return 'Unassigned';
  return `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() || '—';
}

function isUpcoming(a: AppointmentSummary): boolean {
  return a.status === 'scheduled' && new Date(a.starts_at).getTime() > Date.now();
}

export default function CaseAppointmentsPanel({
  caseId,
  registerRefetch,
}: Readonly<Props>): JSX.Element {
  const [items, setItems] = useState<AppointmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);

  const refetch = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const resp = await schedulingService.listAppointments({
        case_id: caseId,
        // No status filter — we render upcoming + past separately
        // so the user can collapse one. Default size on the API is 25,
        // which is plenty for one case's worth of callbacks.
        status: ['scheduled', 'completed', 'cancelled', 'no_show'],
      });
      setItems(resp.items);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not load appointments for this case.'));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  // Expose refetch to the parent so it can re-run on SSE events.
  useEffect(() => {
    if (registerRefetch) registerRefetch(refetch);
  }, [refetch, registerRefetch]);

  const upcoming = items.filter(isUpcoming);
  const past = items.filter((a) => !isUpcoming(a));

  return (
    <section
      className="rounded-2xl p-5"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 className="text-sm font-semibold" style={{ color: colors.muted }}>
          APPOINTMENTS
        </h2>
        <button
          type="button"
          onClick={() => refetch()}
          style={{
            background: 'transparent',
            border: 'none',
            color: colors.muted,
            cursor: 'pointer',
            fontSize: 11,
          }}
          aria-label="Refresh appointments"
        >
          Refresh
        </button>
      </div>

      {error ? (
        <div
          style={{
            marginTop: 8,
            padding: 8,
            borderRadius: 8,
            background: colors.dangerBg,
            color: colors.danger,
            border: `1px solid ${colors.dangerBorder}`,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      ) : null}

      {loading && items.length === 0 ? (
        <p style={{ fontSize: 12, color: colors.muted, marginTop: 8 }}>Loading…</p>
      ) : null}

      {!loading && upcoming.length === 0 && past.length === 0 ? (
        <p style={{ fontSize: 12, color: colors.muted, marginTop: 8 }}>
          No appointments scheduled for this case yet. Use “Schedule callback”
          on an interaction to create one.
        </p>
      ) : null}

      {upcoming.length > 0 ? (
        <ul style={listStyle}>
          {upcoming.map((a) => (
            <li key={a.id} style={itemStyle}>
              <div style={{ fontWeight: 600, fontSize: 13, color: colors.ink }}>
                {fmtDateTime(a.starts_at)}
              </div>
              <div style={{ fontSize: 12, color: colors.inkSoft }}>
                With {partyName(a.agent)} · {a.purpose ?? 'No purpose set'}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {past.length > 0 ? (
        <details
          open={showPast}
          onToggle={(e) => setShowPast((e.target as HTMLDetailsElement).open)}
          style={{ marginTop: 10 }}
        >
          <summary
            style={{
              cursor: 'pointer',
              fontSize: 11,
              color: colors.muted,
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            {past.length} past
          </summary>
          <ul style={listStyle}>
            {past.map((a) => (
              <li key={a.id} style={{ ...itemStyle, opacity: 0.7 }}>
                <div style={{ fontSize: 12, color: colors.ink }}>
                  {fmtDateTime(a.starts_at)} ·{' '}
                  <span style={{ textTransform: 'capitalize' }}>{a.status}</span>
                </div>
                <div style={{ fontSize: 11, color: colors.inkSoft }}>
                  With {partyName(a.agent)} · {a.purpose ?? 'No purpose set'}
                </div>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  marginTop: 10,
  marginBottom: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const itemStyle: React.CSSProperties = {
  padding: 8,
  borderRadius: 8,
  background: colors.surfaceMuted,
};
